import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
/* eslint-disable jsx-a11y/role-supports-aria-props -- a focusable ARIA separator is a range widget; the bundled rule is older than that contract */
import ReactFlow, {
  Background, 
  Controls, 
  MiniMap,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';

import SmartChart from '../../../../../components/SmartChart/SmartChart';
// Toolbox moved to Right Pane

// Custom Nodes
// Custom Nodes
import DataSourceNode from '../nodes/DataSourceNode';
// ChartNode removed (consolidated into ChartOutputNode)
import CandleNode from '../nodes/CandleNode';
import LineChartNode from '../nodes/LineChartNode';
import VolumeChartNode from '../nodes/VolumeChartNode';
import AreaChartNode from '../nodes/AreaChartNode';
import MinimalIndicatorNode from './MinimalIndicatorNode';
import LogicNode from '../nodes/LogicNode';
import SignalNode from '../nodes/SignalNode';
import ChartOutputNode from '../nodes/ChartOutputNode';
import ArithmeticNode from '../nodes/ArithmeticNode';
import HeikinAshiNode from '../nodes/HeikinAshiNode';

import { LAYOUT_CONFIG, getLayoutCategory, calculateNodeHeight, NODE_HEIGHTS, PADDING_Y } from '../layoutConfig';
import { useNodeFactory } from '../hooks/useNodeFactory';
import { CHART_VISUAL_TYPES, GRAPH_EDGE_STYLES, getNodeAccent } from '../config/nodeVisualConfig';
import { useSnackbar } from '../../../../../contexts/SnackbarContext';
import { DEFAULT_OUTLINE_WIDTH, MIN_OUTLINE_WIDTH } from '../hooks/useCanvasViewPrefs';

const outlineMaxFor = (width) => (
  Number.isFinite(width) && width > 0
    ? Math.max(MIN_OUTLINE_WIDTH, Math.floor(width * 0.6))
    : Number.POSITIVE_INFINITY
);

const StrategyLogicStep = ({ 
  selectedInstrument, 
  strategyInfo, 
  onSelectNode, 
  selectedNodeId,
  nodes, 
  edges, 
  onNodesChange, 
  onEdgesChange, 
  setNodes,
  setEdges,
  allIndicatorMetadata = [], // Default to empty array
  graphWiring,
  visible = true,
  focusRequest = 0,
  outlineOpen = false,
  outlineWidth = DEFAULT_OUTLINE_WIDTH,
  onOutlineWidthChange,
  outline = null,
  toolbar = null,
}) => {
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const canvasShellRef = useRef(null);
  const stopOutlineResizeRef = useRef(null);
  const [canvasShellWidth, setCanvasShellWidth] = useState(null);
  const maxOutlineWidth = outlineMaxFor(canvasShellWidth);
  const preferredOutlineWidth = Number.isFinite(outlineWidth) ? outlineWidth : DEFAULT_OUTLINE_WIDTH;
  const renderedOutlineWidth = Math.min(maxOutlineWidth, Math.max(MIN_OUTLINE_WIDTH, preferredOutlineWidth));

  useEffect(() => {
    const shell = canvasShellRef.current;
    if (!shell) return undefined;
    const measure = () => {
      if (Number.isFinite(shell.clientWidth) && shell.clientWidth > 0) setCanvasShellWidth(shell.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => stopOutlineResizeRef.current?.(), []);

  const startOutlineResize = useCallback((event) => {
    event.preventDefault();
    const startX = event.clientX;
    if (!Number.isFinite(startX)) return;
    const startWidth = renderedOutlineWidth;
    const measuredWidth = canvasShellRef.current?.clientWidth;
    const shellWidth = Number.isFinite(measuredWidth) && measuredWidth > 0 ? measuredWidth : canvasShellWidth;
    const dragMax = outlineMaxFor(shellWidth);
    const onMove = (moveEvent) => {
      if (!Number.isFinite(moveEvent.clientX)) return;
      const nextWidth = Math.round(Math.min(
        dragMax,
        Math.max(MIN_OUTLINE_WIDTH, startWidth + moveEvent.clientX - startX),
      ));
      if (Number.isFinite(nextWidth)) onOutlineWidthChange?.(nextWidth);
    };
    const stop = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', stop);
      document.removeEventListener('pointercancel', stop);
      document.removeEventListener('pointerleave', stop);
      stopOutlineResizeRef.current = null;
    };
    stopOutlineResizeRef.current?.();
    stopOutlineResizeRef.current = stop;
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', stop);
    document.addEventListener('pointercancel', stop);
    document.addEventListener('pointerleave', stop);
  }, [canvasShellWidth, onOutlineWidthChange, renderedOutlineWidth]);
  
  const nodeTypes = useMemo(() => ({
    data_source: DataSourceNode,
    chart_node: ChartOutputNode, // Map legacy type to new component to prevent crashes
    chart_output: ChartOutputNode,
    candle_node: CandleNode,
    line_node: LineChartNode,
    area_node: AreaChartNode,
    volume_node: VolumeChartNode,
    heikin_node: HeikinAshiNode,
    indicator: MinimalIndicatorNode,
    logic_gate: LogicNode,
    comparator: LogicNode,
    time_window: LogicNode,   //! #141 — session time-window gate renders as a logic node (boolean output)
    signal: SignalNode,
    position_trigger: SignalNode,
    math: ArithmeticNode
  }), []);
  
  //! graphWiring is required: the parent (StrategyBuilder) owns the ONE shared wiring-hook instance, built on the
  //  role-locked nodes; its functions are main's canvas wiring code.
  const {
    onConnect,
    isValidConnection,
    handleEdgesChange,
    deleteNode,
  } = graphWiring;

  // Handle Drag & Drop from Toolbox
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // --> useNodeFactory Hook
  const { createNode, handleShowSlope } = useNodeFactory(nodes, setNodes, setEdges);
  const { showSnackbar } = useSnackbar();

  // --> Highlight State for Virtual Visualization (Stores ID of the hovered Chart Node)
  const [highlightedPanelId, setHighlightedPanelId] = useState(null);

  // --> Context Menu State for Right Click (Delete Options)
  const [contextMenu, setContextMenu] = useState(null);

  const onEdgeContextMenu = useCallback((event, edge) => {
    event.preventDefault();
    const sourceLocked = nodes.find(node => node.id === edge.source)?.connectable === false;
    const targetLocked = nodes.find(node => node.id === edge.target)?.connectable === false;
    if (sourceLocked || targetLocked) {
      setContextMenu(null);
      return;
    }
    setContextMenu({
      type: 'edge',
      id: edge.id,
      x: event.clientX,
      y: event.clientY
    });
  }, [nodes]);

  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    if (node.deletable === false || node.connectable === false) {
      setContextMenu(null);
      return;
    }
    setContextMenu({
      type: 'node',
      id: node.id,
      x: event.clientX,
      y: event.clientY
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const deleteFromContextMenu = useCallback(() => {
    if (!contextMenu) return;

    if (contextMenu.type === 'edge') {
       handleEdgesChange([{ type: 'remove', id: contextMenu.id }]);
    } else if (contextMenu.type === 'node') {
       const outcome = deleteNode(contextMenu.id);
       if (outcome && !outcome.ok) showSnackbar(outcome.reason, 'info');
    }
    setContextMenu(null);
  }, [contextMenu, handleEdgesChange, deleteNode, showSnackbar]);

  // Hide context menu on global click
  useEffect(() => {
    const handleGlobalClick = () => {
      if (contextMenu) setContextMenu(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, [contextMenu]);

  // --> Full-Chain Highlighting Logic
  const chainEdgeIds = useMemo(() => {
    if (!selectedNodeId) return new Set();

    const chain = new Set();
    const visitedNodes = new Set();

    // Trace UP (Ancestors)
    const traceUp = (nodeId) => {
      if (visitedNodes.has(nodeId)) return;
      visitedNodes.add(nodeId);
      edges.forEach(edge => {
        if (edge.target === nodeId) {
          chain.add(edge.id);
          traceUp(edge.source);
        }
      });
    };

    // Trace DOWN (Descendants)
    const traceDown = (nodeId) => {
      // Re-use visitedNodes for ancestors, but for descendants we just need to avoid cycles
      if (visitedNodes.has(`down-${nodeId}`)) return;
      visitedNodes.add(`down-${nodeId}`);
      edges.forEach(edge => {
        if (edge.source === nodeId) {
          chain.add(edge.id);
          traceDown(edge.target);
        }
      });
    };

    traceUp(selectedNodeId);
    traceDown(selectedNodeId);
    return chain;
  }, [edges, selectedNodeId]);

  // Inject handleShowSlope and Highlight Logic
  const processedNodes = useMemo(() => {
    return nodes.map(node => {
        // 1. Chart Output Node: Inject Hover Handler
        if (node.type === 'chart_output') {
            const panelId = node.data.panel === 'main' ? 'main' : node.id;
            return {
                ...node,
                data: {
                    ...node.data,
                    onHover: (isHovering) => setHighlightedPanelId(isHovering ? panelId : null)
                }
            };
        }

        // 2. Highlighting Logic (Panel Hover OR Selection Chain)
        let shouldHighlight = false;
        
        // A. If selectedNodeId is this node, highlight it
        if (selectedNodeId === node.id) {
            shouldHighlight = true;
        }

        // B. If part of a selected chain (node is an endpoint of a chain edge)
        if (!shouldHighlight && chainEdgeIds.size > 0) {
            shouldHighlight = edges.some(e => chainEdgeIds.has(e.id) && (e.source === node.id || e.target === node.id));
        }
        
        // C. Existing Logic: Panel Hover
        if (!shouldHighlight && highlightedPanelId) {
            const targetId = highlightedPanelId === 'main' 
                ? nodes.find(n => n.type === 'chart_output' && n.data?.panel === 'main')?.id 
                : highlightedPanelId;

            const isConnected = edges.some(e => e.source === node.id && e.target === targetId);
            if (isConnected) shouldHighlight = true;
        }
        
        let newData = { ...node.data };
        let modified = false;

        if (shouldHighlight) {
            newData.isHighlighted = true;
            modified = true;
        } else if (newData.isHighlighted) {
             newData.isHighlighted = false;
             modified = true;
        }

        if (node.type === 'indicator') {
            const isSlopeAttached = nodes.some(n => 
                n.type === 'indicator' && 
                n.data?.indicator === 'dee_trend_slope' && 
                n.data?.sourceId === node.id
            );
            
            newData.onShowSlope = handleShowSlope;
            newData.id = node.id;
            newData.isSlopeAttached = isSlopeAttached;
            modified = true;
        }

        return modified ? { ...node, data: newData } : node;
    });
  }, [nodes, handleShowSlope, highlightedPanelId, selectedNodeId, chainEdgeIds, edges, allIndicatorMetadata]);

  // --> Process Edges: Hide Spaghetti by default, Reveal on Hover/Select
  const processedEdges = useMemo(() => {
      const chartNodeIds = new Set(nodes.filter(n => n.type === 'chart_output').map(n => n.id));
      const mainChartId = nodes.find(n => n.type === 'chart_output' && n.data?.panel === 'main')?.id;
      
      const hasSelectedEdge = edges.some(e => e.selected);

      return edges.map(edge => {
          // Base edge with potential dimming if another edge is selected
          const defaultOpacity = hasSelectedEdge && !edge.selected ? 0.3 : 1;
          // Edge classification colour = the TARGET (destination) node's accent, so a
          // signal edge landing on a TP node reads green, on an SL node red, etc.
          // One place, so loaded + newly-created edges are coloured identically.
          const targetNode = nodes.find(n => n.id === edge.target);
          const targetStroke = targetNode ? getNodeAccent(targetNode.type, targetNode.data) : undefined;
          const baseEdge = {
              ...edge,
              style: {
                  ...edge.style,
                  ...(targetStroke ? { stroke: targetStroke } : {}),
                  opacity: Math.min(edge.style?.opacity ?? 1, defaultOpacity)
              }
          };

          // 0. Explicitly selected edge by user (takes highest visual precedence)
          if (edge.selected) {
              return {
                  ...baseEdge,
                  hidden: false,
                  style: {
                      ...baseEdge.style,
                      stroke: 'var(--warning)', // amber — selection, kept distinct from classification colours
                      strokeWidth: 4,
                      opacity: 1, // Override dimming for the selected edge
                      filter: 'drop-shadow(0 0 4px color-mix(in oklch, var(--warning) 60%, transparent))'
                  }
              };
          }

          // 1. Is part of Selective Highlighting (Selection Chain)?
          if (chainEdgeIds.has(edge.id)) {
              return {
                  ...baseEdge,
                  hidden: false,
                  animated: true,
                  style: { 
                      ...baseEdge.style,
                      ...GRAPH_EDGE_STYLES.SUPER_HIGHLIGHT,
                      opacity: defaultOpacity === 1 ? GRAPH_EDGE_STYLES.SUPER_HIGHLIGHT.opacity || 1 : defaultOpacity
                  }
              };
          }

          // 2. Is targeting a chart node (Panel Hover logic)?
          if (chartNodeIds.has(edge.target)) {
               const isTargetHovered = highlightedPanelId === edge.target || (highlightedPanelId === 'main' && nodes.find(n => n.id === edge.target)?.data?.panel === 'main');
               const sourceNode = nodes.find(n => n.id === edge.source);
               const isPrimaryVisualConnection = sourceNode && CHART_VISUAL_TYPES.includes(sourceNode.type) && edge.target === mainChartId;

               if (isTargetHovered) {
                   return {
                       ...baseEdge,
                       hidden: false,
                       animated: true,
                       style: { 
                           ...baseEdge.style,
                           ...GRAPH_EDGE_STYLES.HIGHLIGHT,
                           opacity: defaultOpacity === 1 ? GRAPH_EDGE_STYLES.HIGHLIGHT.opacity || 1 : defaultOpacity
                       }
                   };
               } else if (isPrimaryVisualConnection) {
                   return {
                       ...baseEdge,
                       hidden: false,
                       animated: true,
                       style: {
                           ...baseEdge.style,
                           ...GRAPH_EDGE_STYLES.DATA_TO_VISUAL,
                           opacity: defaultOpacity === 1 ? GRAPH_EDGE_STYLES.DATA_TO_VISUAL.opacity || 0.8 : defaultOpacity
                       }
                   };
               } else {
                   return {
                       ...baseEdge,
                       hidden: true
                   };
               }
          }
          return baseEdge;
      });
  }, [edges, nodes, highlightedPanelId, chainEdgeIds]);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if (!reactFlowInstance) return;

      const type = event.dataTransfer.getData('application/reactflow');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/nodeData') || '{}');

      if (typeof type === 'undefined' || !type) {
        return;
      }
      
      // Get the position of the drop event relative to the ReactFlow container
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      
      const result = createNode(type, position, nodeData);

      if (!result.success) {
          // Surface singleton / duplicate conflicts to the user (was previously silent).
          if (result.message) showSnackbar(result.message, 'info');
      } else {
          // Auto-select the newly dropped node.
          if (onSelectNode) onSelectNode(result.node);

          // Inform the user when an existing node was silently upgraded to multi_stage.
          if (result.autoUpgraded && result.autoUpgradedLabels?.length > 0) {
              const upgraded = result.autoUpgradedLabels.join(', ');
              showSnackbar(
                  `"${upgraded}" upgraded to Multi-stage → ${result.node.data.label} added as Stage ${result.node.data.stage}`,
                  'info'
              );
          }
      }
    },
    [createNode, onSelectNode, reactFlowInstance, showSnackbar] 
  );
  
  const handleNodeClick = (event, node) => {
      if (onSelectNode) onSelectNode(node);
  };

  const nodesRef = useRef(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  const savedViewportRef = useRef(null);
  const wasVisibleRef = useRef(visible);
  //! the canvas centres ONLY on an explicit focus request (Locate, readiness Fix): selecting a node anywhere else
  //  (Tree/Outline row, chip, properties) never moves it. This ref holds the last request already honoured; a
  //  request made while the canvas is hidden stays pending and is honoured when the canvas is revealed.
  const handledFocusRequestRef = useRef(0);

  useEffect(() => {
    if (!reactFlowInstance) return;
    const wasVisible = wasVisibleRef.current;
    if (wasVisible && !visible) {
      savedViewportRef.current = reactFlowInstance.getViewport?.() || savedViewportRef.current;
    } else if (!wasVisible && visible) {
      const viewport = savedViewportRef.current;
      const pending = focusRequest !== handledFocusRequestRef.current;
      const node = pending ? nodesRef.current.find((candidate) => candidate.id === selectedNodeId) : null;
      if (node) {
        const zoom = viewport?.zoom ?? reactFlowInstance.getViewport?.()?.zoom ?? 1;
        reactFlowInstance.setCenter(node.position.x, node.position.y, { zoom, duration: 300 });
        handledFocusRequestRef.current = focusRequest;
      } else if (viewport) {
        reactFlowInstance.setViewport?.(viewport);
      } else {
        reactFlowInstance.fitView?.();
      }
    }
    wasVisibleRef.current = visible;
  }, [focusRequest, reactFlowInstance, selectedNodeId, visible]);

  useEffect(() => {
    if (!reactFlowInstance || !visible || !selectedNodeId) return;
    if (handledFocusRequestRef.current === focusRequest) return;
    const node = nodesRef.current.find((n) => n.id === selectedNodeId);
    if (!node) return;
    const zoom = reactFlowInstance.getViewport?.()?.zoom ?? savedViewportRef.current?.zoom ?? 1;
    reactFlowInstance.setCenter(node.position.x, node.position.y, { zoom, duration: 300 });
    handledFocusRequestRef.current = focusRequest;
  }, [focusRequest, reactFlowInstance, selectedNodeId, visible]);




  return (
    <div
      className="flex flex-col h-full bg-bg-3 dark:bg-bg-1/50"
      style={visible ? undefined : { visibility: 'hidden', pointerEvents: 'none' }}
      aria-hidden={!visible}
    >
      


      {/* Right-Click Context Menu Layer */}
      {contextMenu && (
        <div 
          className="fixed z-50 bg-white dark:bg-bg-2 rounded-md shadow-lg border border-[var(--divider)] py-1 min-w-[160px] cursor-pointer"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()} // Prevent global click from immediately closing it before action fires
        >
          <button 
            onClick={deleteFromContextMenu}
            className="w-full text-left px-4 py-2.5 text-sm text-loss hover:bg-loss/10 focus:outline-none flex items-center gap-2 font-medium transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
            Delete {contextMenu.type === 'edge' ? 'Connection' : 'Node'}
          </button>
        </div>
      )}

      {/* Bottom: Logic Canvas */}
      <div ref={canvasShellRef} data-testid="strategy-canvas-shell" className="flex-1 flex relative overflow-hidden">
          {outlineOpen && outline && (
            <aside
              data-testid="strategy-outline"
              className="absolute inset-y-0 left-0 z-20 bg-bg-2 border-r border-[var(--divider)]"
              style={{ width: `${renderedOutlineWidth}px` }}
            >
              <div className="h-full overflow-auto">{outline}</div>
              <div
                role="separator"
                aria-label="Resize Outline"
                aria-orientation="vertical"
                aria-valuemin={MIN_OUTLINE_WIDTH}
                aria-valuemax={Number.isFinite(maxOutlineWidth) ? maxOutlineWidth : undefined}
                aria-valuenow={renderedOutlineWidth}
                tabIndex={0}
                onPointerDown={startOutlineResize}
                onKeyDown={(event) => {
                  if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                  event.preventDefault();
                  const delta = event.key === 'ArrowRight' ? 16 : -16;
                  onOutlineWidthChange?.(Math.min(maxOutlineWidth, Math.max(MIN_OUTLINE_WIDTH, renderedOutlineWidth + delta)));
                }}
                className="absolute inset-y-0 right-0 z-30 w-3 translate-x-1/2 cursor-col-resize touch-none focus-visible:bg-primary/20 focus-visible:outline-none"
              />
            </aside>
          )}

          {/* React Flow Canvas */}
          <div className="flex-1 bg-bg-3 dark:bg-bg-1 relative">
              <ReactFlow
                nodes={processedNodes}
                edges={processedEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={handleEdgesChange}
                onConnect={onConnect}
                isValidConnection={isValidConnection}
                onNodeClick={handleNodeClick}
                onPaneClick={() => {
                   if (onSelectNode) onSelectNode(null);
                   closeContextMenu();
                }}
                onEdgeContextMenu={onEdgeContextMenu}
                onNodeContextMenu={onNodeContextMenu}
                //* main's React Flow delete key, only while the Canvas is shown (it stays mounted hidden under Tree/R&D)
                deleteKeyCode={visible ? ['Backspace', 'Delete'] : null}
                nodeTypes={nodeTypes}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onInit={setReactFlowInstance}
                fitView
                className="bg-bg-3 dark:bg-bg-1"
                proOptions={{ hideAttribution: true }}
              >
                {visible && toolbar && (
                  <Panel position="top-left" style={{ left: outlineOpen ? `${renderedOutlineWidth + 8}px` : '8px' }}>
                    {toolbar}
                  </Panel>
                )}
                <Background color="var(--divider)" gap={16} size={1} />
                <Controls
                  className="!bg-bg-2 !border !border-[var(--divider)] !text-fg-3 !shadow-md"
                  style={{ left: outlineOpen ? `${renderedOutlineWidth + 8}px` : '8px' }}
                />
                <MiniMap
                    nodeColor={(n) => getNodeAccent(n.type, n.data)}
                    maskColor="color-mix(in oklch, var(--bg-1) 70%, transparent)"
                    className="!bg-bg-2 !border !border-[var(--divider)]"
                />
              </ReactFlow>
          </div>
      </div>
    </div>
  );
};

export default React.memo(StrategyLogicStep);
